import {
  type CreateInvoiceDraftInput,
  type Invoice,
  type InvoiceBasis,
  type InvoiceStatus,
  type Rial,
} from "@kitchen/schemas";
import { Button, Chip, faDigits, formatDate, formatMoney } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, financeApi, type TreeNode } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";
import { PrintInvoiceView } from "./print.js";

export function InvoicesTab() {
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus | "all">("all");
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");

  const [activeInvoice, setActiveInvoice] = useState<(Invoice & { version: number }) | null>(null);
  const [printingInvoice, setPrintingInvoice] = useState<Invoice | null>(null);

  // Draft Creation Form State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [draftOrgId, setDraftOrgId] = useState("");
  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");
  const [draftBasis, setDraftBasis] = useState<InvoiceBasis | "default">("default");
  const [draftNotes, setDraftNotes] = useState("");
  const [draftPreviewResult, setDraftPreviewResult] = useState<{
    invoice: Invoice;
    unpricedLines: unknown[];
  } | null>(null);

  // Revision Modal State
  const [showReviseModal, setShowReviseModal] = useState(false);
  const [reviseReason, setReviseReason] = useState("");
  const [reviseDiscountAmount, setReviseDiscountAmount] = useState<number>(0);
  const [reviseDiscountReason, setReviseDiscountReason] = useState("");
  const [reviseProblemId, setReviseProblemId] = useState("");

  const tree = useQuery({ queryKey: ["organizations"], queryFn: () => api<TreeNode[]>("/organizations") });

  const orgList: { id: string; name: string }[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      orgList.push({ id: n.id, name: n.name });
      walk(n.children);
    }
  };
  walk(tree.data ?? []);

  const getOrgName = (id: string) => orgList.find((o) => o.id === id)?.name ?? "سازمان";

  const invoicesQuery = useQuery({
    queryKey: ["finance", "invoices", selectedOrgId, selectedStatus, periodStart, periodEnd],
    queryFn: () =>
      financeApi.listInvoices({
        organizationId: selectedOrgId || undefined,
        status: selectedStatus === "all" ? undefined : selectedStatus,
        periodStart: periodStart || undefined,
        periodEnd: periodEnd || undefined,
      }),
  });

  // Action: Create Draft
  const createDraftAction = useAction(
    async (input: CreateInvoiceDraftInput) => {
      const res = await financeApi.createDraft(input);
      setDraftPreviewResult(res);
      setActiveInvoice({ ...res.invoice, version: res.version });
      setShowCreateModal(false);
    },
    [["finance", "invoices"]],
  );

  // Action: Recompute Draft
  const recomputeAction = useAction(
    async (inv: Invoice & { version: number }) => {
      const res = await financeApi.recomputeDraft(inv.id, { version: inv.version });
      setActiveInvoice({ ...res.invoice, version: res.version });
    },
    [["finance", "invoices"]],
  );

  // Action: Issue Draft
  const issueAction = useAction(
    async ({ id, version, notes }: { id: string; version: number; notes?: string }) => {
      const res = await financeApi.issueInvoice(id, { version, notes });
      setActiveInvoice({ ...res.invoice, version: res.version });
    },
    [
      ["finance", "invoices"],
      ["finance", "statement"],
    ],
  );

  // Action: Revise Issued
  const reviseAction = useAction(
    async ({
      id,
      version,
      reason,
      discountAmount,
      discountReason,
      problemId,
    }: {
      id: string;
      version: number;
      reason: string;
      discountAmount?: number;
      discountReason?: string;
      problemId?: string;
    }) => {
      const discounts =
        discountAmount && discountAmount > 0
          ? [
              {
                id: crypto.randomUUID(),
                scope: "invoice" as const,
                lineId: null,
                amount: discountAmount as Rial,
                reason: discountReason || reason,
              },
            ]
          : undefined;

      const res = await financeApi.reviseInvoice(id, {
        version,
        reason,
        discounts,
        problemId: problemId || undefined,
      });
      setActiveInvoice({ ...res.newRevision, version: 1 });
      setShowReviseModal(false);
      setReviseReason("");
      setReviseDiscountAmount(0);
      setReviseDiscountReason("");
    },
    [
      ["finance", "invoices"],
      ["finance", "statement"],
    ],
  );

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Filters & Header Bar */}
      <div className="panel">
        <div className="row wrap-between" style={{ alignItems: "center", gap: "var(--s3)" }}>
          <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
            <div>
              <label htmlFor="inv-filter-org" className="label t-cap muted">
                سازمان
              </label>
              <select
                id="inv-filter-org"
                className="input"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                }}
              >
                <option value="">همه سازمان‌ها</option>
                {orgList.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="inv-filter-status" className="label t-cap muted">
                وضعیت
              </label>
              <select
                id="inv-filter-status"
                className="input"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value as InvoiceStatus | "all");
                }}
              >
                <option value="all">همه وضعیت‌ها</option>
                <option value="draft">پیش‌نویس (صادرنشده)</option>
                <option value="issued">صادر شده (نهایی)</option>
              </select>
            </div>

            <div>
              <label htmlFor="inv-filter-start" className="label t-cap muted">
                از تاریخ
              </label>
              <input
                id="inv-filter-start"
                type="date"
                className="input"
                value={periodStart}
                onChange={(e) => {
                  setPeriodStart(e.target.value);
                }}
              />
            </div>

            <div>
              <label htmlFor="inv-filter-end" className="label t-cap muted">
                تا تاریخ
              </label>
              <input
                id="inv-filter-end"
                type="date"
                className="input"
                value={periodEnd}
                onChange={(e) => {
                  setPeriodEnd(e.target.value);
                }}
              />
            </div>
          </div>

          <Button
            variant="primary"
            icon="wallet"
            onClick={() => {
              setShowCreateModal(true);
              setDraftPreviewResult(null);
            }}
          >
            صدور فاکتور دوره‌ای جدید
          </Button>
        </div>
      </div>

      {/* Invoices List */}
      <div className="panel" title="فهرست صورتحساب‌ها و پیش‌نویس‌ها">
        {invoicesQuery.isLoading && <p className="muted">در حال بارگذاری فاکتورها...</p>}
        {invoicesQuery.isError && <ErrorLine error="خطا در دریافت فهرست فاکتورها" />}

        {invoicesQuery.data && invoicesQuery.data.length === 0 && (
          <p className="muted text-center" style={{ padding: "var(--s6) 0" }}>
            فاکتوری با فیلترهای مشخص‌شده یافت نشد.
          </p>
        )}

        {invoicesQuery.data && invoicesQuery.data.length > 0 && (
          <table className="table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>شماره فاکتور</th>
                <th>سازمان</th>
                <th>دوره مصرف</th>
                <th>مبنا</th>
                <th>تعداد اقلام</th>
                <th>مبلغ کل (ریال)</th>
                <th>وضعیت</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {invoicesQuery.data.map((inv) => (
                <tr key={inv.id}>
                  <td className="bold">{faDigits(inv.number)}</td>
                  <td>{getOrgName(inv.organizationId)}</td>
                  <td>
                    {formatDate(inv.periodStart)} تا {formatDate(inv.periodEnd)}
                  </td>
                  <td>
                    <Chip plain>{inv.basis === "delivered" ? "تحویل شده" : "سفارش"}</Chip>
                  </td>
                  <td>{faDigits(inv.lines.length)} ردیف</td>
                  <td className="bold">{formatMoney(inv.totalAmount, "rial")}</td>
                  <td>
                    {inv.status === "issued" ? (
                      <Chip tone="ok">صادر شده (ویرایش {faDigits(inv.revision)})</Chip>
                    ) : (
                      <Chip tone="warn">پیش‌نویس</Chip>
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: "var(--s2)" }}>
                      <Button
                        size="md"
                        onClick={() => {
                          void financeApi.getInvoice(inv.id).then(setActiveInvoice);
                        }}
                      >
                        جزئیات
                      </Button>
                      {inv.status === "issued" && (
                        <Button
                          size="md"
                          variant="ghost"
                          onClick={() => {
                            setPrintingInvoice(inv);
                          }}
                        >
                          چاپ A4
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal: Create Draft Invoice */}
      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "600px", width: "90%" }}>
            <h2 className="t-title">صدور پیش‌نویس فاکتور دوره‌ای</h2>
            <p className="muted t-cap">
              فاکتور بر اساس سفارش‌های تحویل شده/سفارش داده شده در بازه زمانی تعیین‌شده تولید می‌شود.
            </p>

            <div className="stack" style={{ gap: "var(--s3)", margin: "var(--s4) 0" }}>
              <div>
                <label htmlFor="draft-org" className="label t-cap">
                  انتخاب سازمان
                </label>
                <select
                  id="draft-org"
                  className="input"
                  value={draftOrgId}
                  onChange={(e) => {
                    setDraftOrgId(e.target.value);
                  }}
                >
                  <option value="">-- انتخاب کنید --</option>
                  {orgList.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="row" style={{ gap: "var(--s3)" }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="draft-start" className="label t-cap">
                    از تاریخ (شروع دوره)
                  </label>
                  <input
                    id="draft-start"
                    type="date"
                    className="input"
                    value={draftStart}
                    onChange={(e) => {
                      setDraftStart(e.target.value);
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="draft-end" className="label t-cap">
                    تا تاریخ (پایان دوره)
                  </label>
                  <input
                    id="draft-end"
                    type="date"
                    className="input"
                    value={draftEnd}
                    onChange={(e) => {
                      setDraftEnd(e.target.value);
                    }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="draft-basis" className="label t-cap">
                  مبنای محاسبه فاکتور
                </label>
                <select
                  id="draft-basis"
                  className="input"
                  value={draftBasis}
                  onChange={(e) => {
                    setDraftBasis(e.target.value as InvoiceBasis | "default");
                  }}
                >
                  <option value="default">پیش‌فرض سازمان</option>
                  <option value="delivered">تعداد تحویل شده (پیش‌فرض سیستم)</option>
                  <option value="ordered">تعداد سفارش داده شده</option>
                </select>
              </div>

              <div>
                <label htmlFor="draft-notes" className="label t-cap">
                  یادداشت فاکتور (اختیاری)
                </label>
                <input
                  id="draft-notes"
                  type="text"
                  className="input"
                  placeholder="مثال: فاکتور نیمه اول آبان‌ماه ۱۴۰۵"
                  value={draftNotes}
                  onChange={(e) => {
                    setDraftNotes(e.target.value);
                  }}
                />
              </div>

              <ErrorLine error={createDraftAction.error} />

              {draftPreviewResult?.unpricedLines && draftPreviewResult.unpricedLines.length > 0 && (
                <div className="alert coral" style={{ padding: "var(--s3)" }}>
                  <span className="bold">هشدار خطوط بدون قیمت:</span>{" "}
                  {faDigits(draftPreviewResult.unpricedLines.length)} ردیف بدون قیمت یافت شد. صدور نهایی
                  فاکتور تا زمان قیمت‌گذاری امکان‌پذیر نخواهد بود.
                </div>
              )}
            </div>

            <div className="row wrap" style={{ justifyContent: "flex-end", gap: "var(--s3)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCreateModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={!draftOrgId || !draftStart || !draftEnd || createDraftAction.busy}
                onClick={() => {
                  createDraftAction.run({
                    organizationId: draftOrgId,
                    periodStart: draftStart,
                    periodEnd: draftEnd,
                    basis: draftBasis === "default" ? undefined : draftBasis,
                    notes: draftNotes || undefined,
                  });
                }}
              >
                {createDraftAction.busy ? "در حال ایجاد..." : "تولید پیش‌نویس فاکتور"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer / Modal: Invoice Details */}
      {activeInvoice && (
        <div className="modal-backdrop">
          <div
            className="card modal-card"
            style={{ maxWidth: "850px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
          >
            <div className="row wrap-between" style={{ alignItems: "center" }}>
              <div>
                <h2 className="t-title">
                  جزئیات فاکتور {faDigits(activeInvoice.number)}
                  {activeInvoice.status === "issued" && ` (ویرایش ${faDigits(activeInvoice.revision)})`}
                </h2>
                <p className="muted t-cap">
                  {getOrgName(activeInvoice.organizationId)} · دوره {formatDate(activeInvoice.periodStart)} تا{" "}
                  {formatDate(activeInvoice.periodEnd)}
                </p>
              </div>
              <div className="row" style={{ gap: "var(--s2)" }}>
                {activeInvoice.status === "issued" ? (
                  <Chip tone="ok">صادر شده</Chip>
                ) : (
                  <Chip tone="warn">پیش‌نویس (قابل ویرایش)</Chip>
                )}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setActiveInvoice(null);
                  }}
                >
                  بستن
                </Button>
              </div>
            </div>

            <div className="stack" style={{ gap: "var(--s3)", margin: "var(--s4) 0" }}>
              <div className="card" style={{ backgroundColor: "var(--bg-subtle)" }}>
                <div className="row wrap-between">
                  <div>
                    <span className="muted t-cap">مبنا: </span>
                    <span className="bold">
                      {activeInvoice.basis === "delivered" ? "تحویل شده" : "سفارش"}
                    </span>
                  </div>
                  <div>
                    <span className="muted t-cap">کل اقلام: </span>
                    <span className="bold">{faDigits(activeInvoice.totalBilledQuantity)} پرس</span>
                  </div>
                  <div>
                    <span className="muted t-cap">مبلغ نهایی: </span>
                    <span className="bold" style={{ color: "var(--blue-ink)" }}>
                      {formatMoney(activeInvoice.totalAmount, "rial")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Lines Table */}
              <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                <table className="table" style={{ width: "100%", fontSize: "13px" }}>
                  <thead>
                    <tr>
                      <th>تاریخ</th>
                      <th>شرح</th>
                      <th>سفارش</th>
                      <th>تحویل</th>
                      <th>مبنا</th>
                      <th>قیمت واحد</th>
                      <th>جمع جزء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeInvoice.lines.map((l) => (
                      <tr key={l.id}>
                        <td>{formatDate(l.businessDate)}</td>
                        <td>{l.isCompanion ? "مخلفات" : "غذای اصلی"}</td>
                        <td>{faDigits(l.orderedQuantity)}</td>
                        <td>{l.deliveredQuantity !== null ? faDigits(l.deliveredQuantity) : "—"}</td>
                        <td className="bold">{faDigits(l.billedQuantity)}</td>
                        <td>{formatMoney(l.unitPrice, "rial")}</td>
                        <td className="bold">{formatMoney(l.totalAmount, "rial")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ErrorLine error={recomputeAction.error || issueAction.error || reviseAction.error} />
            </div>

            {/* Footer Actions */}
            <div
              className="row wrap-between"
              style={{ alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: "var(--s3)" }}
            >
              <div>
                {activeInvoice.status === "issued" && (
                  <Button
                    variant="ghost"
                    icon="printer"
                    onClick={() => {
                      setPrintingInvoice(activeInvoice);
                    }}
                  >
                    چاپ A4
                  </Button>
                )}
              </div>

              <div className="row" style={{ gap: "var(--s3)" }}>
                {activeInvoice.status === "draft" && (
                  <>
                    <Button
                      variant="default"
                      disabled={recomputeAction.busy}
                      onClick={() => {
                        recomputeAction.run(activeInvoice);
                      }}
                    >
                      {recomputeAction.busy ? "در حال محاسبه..." : "محاسبه مجدد"}
                    </Button>
                    <Button
                      variant="primary"
                      disabled={issueAction.busy}
                      onClick={() => {
                        issueAction.run({ id: activeInvoice.id, version: activeInvoice.version });
                      }}
                    >
                      {issueAction.busy ? "در حال صدور..." : "صدور نهایی فاکتور"}
                    </Button>
                  </>
                )}

                {activeInvoice.status === "issued" && (
                  <Button
                    variant="urgent"
                    onClick={() => {
                      setShowReviseModal(true);
                    }}
                  >
                    اصلاح فاکتور (ثبت ویرایش و سند تعدیل)
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Revise Issued Invoice */}
      {showReviseModal && activeInvoice && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "550px", width: "90%" }}>
            <h2 className="t-title">اصلاح فاکتور صادرشده {faDigits(activeInvoice.number)}</h2>
            <p className="muted t-cap">
              فاکتور صادرشده مستقیماً تغییر نمی‌کند؛ نسخه جدید ({faDigits(activeInvoice.revision + 1)}) همراه
              با یک یادداشت بدهکار یا بستانکار صادر خواهد شد.
            </p>

            <div className="stack" style={{ gap: "var(--s3)", margin: "var(--s4) 0" }}>
              <div>
                <label htmlFor="rev-reason" className="label t-cap">
                  علت اصلاح (اجباری)
                </label>
                <input
                  id="rev-reason"
                  type="text"
                  className="input"
                  placeholder="مثال: گزارش کسری تحویل توسط راننده یا توافق تخفیف"
                  value={reviseReason}
                  onChange={(e) => {
                    setReviseReason(e.target.value);
                  }}
                />
              </div>

              <div>
                <label htmlFor="rev-discount" className="label t-cap">
                  مبلغ کسورات / تخفیف تعدیل (ریال - در صورت وجود)
                </label>
                <input
                  id="rev-discount"
                  type="number"
                  className="input"
                  value={reviseDiscountAmount || ""}
                  onChange={(e) => {
                    setReviseDiscountAmount(Number(e.target.value));
                  }}
                />
              </div>

              {reviseDiscountAmount > 0 && (
                <div>
                  <label htmlFor="rev-disc-reason" className="label t-cap">
                    شرح تخفیف / کسری
                  </label>
                  <input
                    id="rev-disc-reason"
                    type="text"
                    className="input"
                    value={reviseDiscountReason}
                    onChange={(e) => {
                      setReviseDiscountReason(e.target.value);
                    }}
                  />
                </div>
              )}

              <div>
                <label htmlFor="rev-problem" className="label t-cap">
                  شناسه مشکل توزیع (اختیاری)
                </label>
                <input
                  id="rev-problem"
                  type="text"
                  className="input"
                  placeholder="UUID مشکل گزارش‌شده توسط راننده"
                  value={reviseProblemId}
                  onChange={(e) => {
                    setReviseProblemId(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="row wrap" style={{ justifyContent: "flex-end", gap: "var(--s3)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowReviseModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="urgent"
                disabled={!reviseReason || reviseAction.busy}
                onClick={() => {
                  reviseAction.run({
                    id: activeInvoice.id,
                    version: activeInvoice.version,
                    reason: reviseReason,
                    discountAmount: reviseDiscountAmount,
                    discountReason: reviseDiscountReason,
                    problemId: reviseProblemId,
                  });
                }}
              >
                {reviseAction.busy ? "در حال ثبت اصلاحیه..." : "تأیید و صدور ویرایش جدید"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* A4 Print View */}
      {printingInvoice && (
        <PrintInvoiceView
          invoice={printingInvoice}
          organizationName={getOrgName(printingInvoice.organizationId)}
          onClose={() => {
            setPrintingInvoice(null);
          }}
        />
      )}
    </div>
  );
}
